import * as React from 'react'
import { useParams } from 'react-router-dom'

import { useEffect } from 'modules/core/hooks/useEffect'

import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import View from 'modules/shift_management/store/internal/models/View'
import {
  DEFAULT_GROUP_TYPE,
  DEFAULT_VIEW_TYPE,
  type GroupType,
  type ViewType,
  isValidGroupType,
  isValidViewType,
} from 'modules/shift_management/store/internal/models/View'

import { periodFromParams } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/periodFromParams'

import { useEnabledFeatures } from './useEnabledFeatures'
import useIsTimeFormat24H from './useIsTimeFormat24H'

/**
 * Syncs downstream stores (schedule, selection, shifts rows, pagination) when the
 * URL view params change via programmatic navigation that does not go through
 * onChangePeriod / onChangeRoute (e.g. AI-driven FilterEmployeeListActivity).
 *
 * Without this, the calendar grid keeps the previous period's data while the
 * period selector — which reads view directly from URL params — already shows
 * the new period.
 *
 * Mounted once at the top of the page (MainPage) to avoid duplicate dispatches
 * from each useChangeView consumer.
 */
const useNavigationSync = () => {
  const [, dispatchSchedule] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchPagination] = usePagination()
  const [, dispatchFilters] = useFilters()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const params = useParams<{
    viewType: ViewType
    groupType: GroupType
    year: string
    month: string
    day: string
  }>()

  const { isTimeFormat24H } = useIsTimeFormat24H()
  const [enabledFeatures] = useEnabledFeatures()
  const featureFlags = enabledFeatures.featureFlags

  // Skip dispatches at mount: the providers already initialised with the URL state.
  const lastSyncedParamsRef = React.useRef<string>(
    `${params.viewType}/${params.groupType}/${params.year}/${params.month}/${params.day}`
  )

  useEffect(() => {
    const currentParamsKey = `${params.viewType}/${params.groupType}/${params.year}/${params.month}/${params.day}`
    if (currentParamsKey === lastSyncedParamsRef.current) return
    lastSyncedParamsRef.current = currentParamsKey

    const viewType: ViewType = isValidViewType(params.viewType)
      ? params.viewType
      : DEFAULT_VIEW_TYPE
    const groupType: GroupType = isValidGroupType(params.groupType)
      ? params.groupType
      : DEFAULT_GROUP_TYPE
    const date = periodFromParams(params, viewType)
    const view = new View({ date, viewType, groupType, isTimeFormat24H, featureFlags })

    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view })
    dispatchSchedule({ type: 'SET_STATS_LOADING', statsLoading: true })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }, [params.viewType, params.groupType, params.year, params.month, params.day]) // eslint-disable-line react-hooks/exhaustive-deps
}

export default useNavigationSync
